// ---------------------------------------------------------------------------
// FeltonChannels - Channels Management Page (V11)
// ---------------------------------------------------------------------------
// Shows client's own social media channels with performance and processing metrics
// Follows Discovery pattern but simplified (no time filters, no more filters)
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { Radio, Clock } from 'lucide-react';
import { ChannelsFilterRow } from '@/components/v11/channels/ChannelsFilterRow';
import { DataTable } from '@/components/v11/table';
import type { Table } from '@tanstack/react-table';
import { channelsService } from '@/services/v11/channelsService';
import type { Channel } from '@/services/v11/channelsService';
import { getColumnsForSource } from '@/data/explorerColumns';
import { GlobalTimeBar, DataLoader } from '@/components/v11/discovery';
import type { ViewMode, TimePeriod } from '@/components/v11/discovery';
import { ChannelCardGrid } from '@/components/v11/channels';
import { AccountHealthZone } from '@/components/v11/accountHealth';
import type { MiniChartDataByMetricMap, ChannelMiniChartEntry } from '@/components/v11/channels';
import type {
  ChannelCardSparklineData,
  SparklineMetric,
} from '@/components/v11/channels/card/ChannelCardSparkline';
import { useV11Layout } from '@/contexts/V11LayoutContext';
import { periodToDates } from '@/lib/periodToDates';
import { periodToGranularity } from '@/lib/periodToGranularity';
import { readStoredChannelsPeriod, writeStoredChannelsPeriod } from './channelsPeriodStorage';

const DEFAULT_PAGE_SIZE = 25;

export function FeltonChannels() {
  const { setHeaderControls } = useV11Layout();
  const [searchParams, setSearchParams] = useSearchParams();

  // Include Own Channels toggle (URL-persisted: ioc=1 means show own channels)
  const includeOwnChannels = searchParams.get('ioc') === '1';
  const setIncludeOwnChannels = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('ioc', '1');
      } else {
        next.delete('ioc');
      }
      return next;
    });
  }, [setSearchParams]);

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;
  const [sortBy, setSortBy] = useState<string>('totalPosts');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>('chart');
  // Client-side filters (shared between Cards and Table views)
  const [channelSearch, setChannelSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<ReadonlyArray<string>>([]);
  // Period persistence (Q8): restore the last chosen preset on mount.
  // Custom ranges deliberately do not persist — they reset to the default
  // on every fresh visit (see channelsPeriodStorage.writeStoredChannelsPeriod).
  const [period, setPeriod] = useState<TimePeriod>(() => readStoredChannelsPeriod());
  const [customDateFrom, setCustomDateFrom] = useState<string | undefined>();
  const [customDateTo, setCustomDateTo] = useState<string | undefined>();

  useEffect(() => {
    writeStoredChannelsPeriod(period);
  }, [period]);

  // Convert period to date range for API
  const periodDates = useMemo(() => {
    if (period === 'custom' && customDateFrom && customDateTo) {
      return { date_from: customDateFrom, date_to: customDateTo };
    }
    return periodToDates(period);
  }, [period, customDateFrom, customDateTo]);

  // Inject GlobalTimeBar into layout header
  useEffect(() => {
    setHeaderControls(
      <GlobalTimeBar
        selectedPeriod={period}
        onPeriodChange={setPeriod}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        onCustomDatesChange={(from: string, to: string) => {
          setPeriod('custom');
          setCustomDateFrom(from);
          setCustomDateTo(to);
        }}
        onCustomDatesClear={() => {
          setCustomDateFrom(undefined);
          setCustomDateTo(undefined);
        }}
      />
    );
    return () => setHeaderControls(null);
  }, [period, customDateFrom, customDateTo, setHeaderControls]);

  // Fetch channels
  const {
    data: channelsData,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['v11-channels', currentPage, pageSize, sortBy, sortOrder, includeOwnChannels],
    queryFn: async () => {
      const result = await channelsService.getChannels({
        limit: pageSize,
        page: currentPage,
        sort: sortBy,
        order: sortOrder,
        include_own_channels: includeOwnChannels ? 'true' : 'false',
      });
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Fetch mini-chart data for all 4 metrics in parallel. Keeps the card's
  // metric selector instant (no network round-trip on pill click). React
  // Query caches per metric + window, so stale data is reused across
  // re-renders and tab switches.
  const METRICS: readonly SparklineMetric[] = ['interactions', 'posts', 'likes', 'comments'];
  const granularity = periodToGranularity(period, periodDates.date_from, periodDates.date_to);
  const trendQueries = useQueries({
    queries: METRICS.map(metric => ({
      queryKey: [
        'v11-channels-minichart',
        metric,
        periodDates.date_from,
        periodDates.date_to,
        includeOwnChannels,
        granularity,
      ] as const,
      queryFn: () =>
        channelsService.getTrends(metric, {
          date_from: periodDates.date_from,
          date_to: periodDates.date_to,
          granularity,
          include_own_channels: includeOwnChannels ? 'true' : 'false',
        }),
      enabled: viewMode === 'chart',
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  // Time slots are now derived from the BE response (see miniChartData useMemo).
  // This eliminates DST/timezone misalignment between FE-generated slots and BE periods.

  // Human-readable period label for card context (e.g. "Mar 2025 — Mar 2026")
  const periodLabel = useMemo(() => {
    if (!periodDates.date_from || !periodDates.date_to) return undefined;
    const fmt = (d: string) => {
      const date = new Date(d);
      return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    };
    return `${fmt(periodDates.date_from)} — ${fmt(periodDates.date_to)}`;
  }, [periodDates]);

  /**
   * Transform the 4 trend queries into a map keyed by "username (Platform)"
   * with all 4 metrics per channel. Each channel gets a fixed month-slot
   * array so the sparkline X-axis is consistent regardless of gaps.
   */
  const miniChartData = useMemo((): MiniChartDataByMetricMap => {
    type MutablePartial = {
      -readonly [K in keyof ChannelCardSparklineData]?: ChannelCardSparklineData[K];
    };
    const byChannel: Record<string, MutablePartial> = {};

    // Use the first resolved trend query to extract time slots from the BE response.
    // This ensures FE slots always match BE periods (no DST/timezone misalignment).
    const refTimeline = trendQueries.find(q => q.data?.data?.timeline?.length)?.data?.data?.timeline ?? [];
    const beSlots = refTimeline.map((point: Record<string, unknown>) => ({
      key: (point.period as string) || '',
      label: (point.periodLabel as string) || '',
    }));

    METRICS.forEach((metric, idx) => {
      const timeline = trendQueries[idx]?.data?.data?.timeline ?? [];
      // Collect raw {channelKey: {period: value}}
      const raw: Record<string, Record<string, number>> = {};
      for (const point of timeline) {
        const periodStr = (point.period as string) || '';
        for (const [key, value] of Object.entries(point)) {
          if (key === 'period' || key === 'periodLabel') continue;
          const numValue = typeof value === 'number' ? value : 0;
          if (!raw[key]) raw[key] = {};
          raw[key][periodStr] = (raw[key][periodStr] ?? 0) + numValue;
        }
      }

      // Fill every channel's slot array for this metric using BE-provided slots
      for (const [channelKey, slotValues] of Object.entries(raw)) {
        const series: ChannelMiniChartEntry[] = beSlots.map(({ key, label }: { key: string; label: string }) => ({
          day: label,
          posts: slotValues[key] ?? 0,
        }));
        if (!byChannel[channelKey]) byChannel[channelKey] = {};
        byChannel[channelKey][metric] = series;
      }
    });

    // Normalise every channel to have all 4 metric slots (empty fallback).
    const map: Record<string, ChannelCardSparklineData> = {};
    for (const [channelKey, partial] of Object.entries(byChannel)) {
      map[channelKey] = {
        interactions: partial.interactions ?? [],
        posts: partial.posts ?? [],
        likes: partial.likes ?? [],
        comments: partial.comments ?? [],
      };
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendQueries.map(q => q.data)]);

  const isSparklineLoading = trendQueries.some(q => q.isLoading);

  // Handlers
  const handleSortChange = useCallback((sorting: Array<{ id: string; desc: boolean }>) => {
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      setSortBy(id);
      setSortOrder(desc ? 'desc' : 'asc');
      setCurrentPage(1);
    }
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  const handleColumnVisibilityChange = useCallback((keys: string[]) => {
    setVisibleColumnKeys(keys);
  }, []);

  const handleTableReady = useCallback((table: Table<unknown>) => {
    const keys = table.getVisibleLeafColumns().map(col => col.id);
    setVisibleColumnKeys(keys);
  }, []);

  const columns = useMemo(() => {
    const discoveryLinkMap: Record<string, string> = {
      totalPosts: 'content',
      commentsReceived: 'comments',
      profilesAdded: 'profiles',
    };

    return getColumnsForSource('channels').map(col => {
      const key = (col as { accessorKey?: string }).accessorKey;
      const source = key ? discoveryLinkMap[key] : undefined;
      if (!source) return col;

      return {
        ...col,
        cell: (info: CellContext<unknown, unknown>) => {
          const row = info.row.original as Channel;
          const value = info.getValue() as number;
          const meta = info.table.options.meta as import('@/components/v11/table/types').TableMeta | undefined;
          const p = meta?.currentPeriod ?? '30d';
          const periodParam = p !== '30d' ? `&period=${p}` : '';
          const sourceParam = source !== 'profiles' ? `&source=${source}` : '';
          const href = `/v11/discovery?f_channel=${row.authorId}&f_channel_labels=${encodeURIComponent(row.username)}&f_channel_platforms=${encodeURIComponent(row.platform)}${periodParam}${sourceParam}`;

          const formatted = value != null ? value.toLocaleString() : '—';
          return React.createElement(
            Link,
            { to: href, title: 'View in Discovery', className: 'text-v11-accent hover:text-v11-accent/80 hover:underline font-medium tabular-nums' },
            formatted
          );
        },
      } as ColumnDef<unknown>;
    });
  }, []);

  // Channel IDs for period metrics query (derived from channels response)
  // Fetch period metrics in parallel with channels (uses account_id from auth, no channel_ids needed)
  const {
    data: periodMetricsData,
    isFetching: isPeriodMetricsFetching,
  } = useQuery({
    queryKey: ['v11-channels-period-metrics', periodDates, includeOwnChannels],
    queryFn: () =>
      channelsService.getPeriodMetrics({
        date_from: periodDates.date_from!,
        date_to: periodDates.date_to!,
        include_own_channels: includeOwnChannels ? 'true' : 'false',
      }),
    enabled: !!periodDates.date_from && !!periodDates.date_to,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Merge channels with period metrics (progressive enrichment)
  // When period metrics are available, override all-time fields so both
  // the card view and the table view show the same period-scoped values.
  const currentData = useMemo(() => {
    if (!channelsData?.data) return [];
    const periodMap = periodMetricsData?.data;
    if (!periodMap) return [...channelsData.data];

    return channelsData.data.map(ch => {
      const pm = periodMap[String(ch.datasourceItemId)];
      if (!pm) return { ...ch };
      return {
        ...ch,
        period: pm,
        totalPosts: pm.postsInPeriod,
        totalLikes: pm.likesInPeriod,
        commentsReceived: pm.commentsInPeriod,
        profilesAdded: pm.profilesAddedInPeriod,
      };
    });
  }, [channelsData, periodMetricsData]);

  const isPeriodLoading = isPeriodMetricsFetching && !periodMetricsData;

  // Extract unique platforms for filter dropdown
  const platforms = useMemo(() => {
    const set = new Set(currentData.map(ch => ch.platform));
    return Array.from(set).sort();
  }, [currentData]);

  // Client-side filtered data (for Table view — matches Cards filter logic)
  const filteredData = useMemo(() => {
    const lowerSearch = channelSearch.toLowerCase();
    return currentData.filter(ch => {
      if (
        lowerSearch &&
        !ch.username.toLowerCase().includes(lowerSearch) &&
        !(ch.fullName ?? '').toLowerCase().includes(lowerSearch) &&
        !ch.datasourceName.toLowerCase().includes(lowerSearch)
      ) {
        return false;
      }
      if (platformFilter !== 'all' && ch.platform !== platformFilter) return false;
      if (statusFilter === 'active' && !ch.scrapingEnabled) return false;
      if (statusFilter === 'paused' && ch.scrapingEnabled) return false;
      return true;
    });
  }, [currentData, channelSearch, platformFilter, statusFilter]);

  // No discovery: show "coming soon" when no channels exist (account has no discovery data)
  const hasNoDiscovery = !isLoading && !error && currentData.length === 0;

  return (
    <div className="space-y-6">
      {/* Zone 1 — Account Health (collapsible, auto-expands when not healthy) */}
      <AccountHealthZone />

      {/* Header — icon + title + subtitle only. View-mode toggle lives in
          the ChannelCardGrid filter row (cards mode) or above the DataTable
          (table mode) so it reads as a Zone-2 control, not a page-global one. */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
          <Radio className="h-6 w-6 text-cyan-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-v11-text-primary">Channels Management</h1>
          <p className="text-sm text-v11-text-muted">
            Monitor your social media channels - performance and processing metrics
          </p>
        </div>
      </div>

      {/* Coming Soon - No Discovery */}
      {hasNoDiscovery ? (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-cyan-500/10 flex items-center justify-center">
              <Clock className="w-7 h-7 text-cyan-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-v11-text-primary mb-2">Coming Soon</h3>
              <p className="text-sm text-v11-text-muted max-w-md mx-auto">
                Channels management will be available soon. We're preparing everything so you can
                monitor your social media channels.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Table View */}
          {viewMode === 'table' && (
            <>
              <ChannelsFilterRow
                search={channelSearch}
                onSearchChange={setChannelSearch}
                platformFilter={platformFilter}
                onPlatformFilterChange={setPlatformFilter}
                platforms={platforms}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                includeOwnChannels={includeOwnChannels}
                onIncludeOwnChannelsChange={setIncludeOwnChannels}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
              />
              {isLoading ? (
                <DataLoader dataSource="channels" variant="initial" />
              ) : error ? (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-8 text-center">
                  <p className="text-sm font-medium text-red-400">Failed to load channels</p>
                  <p className="text-xs text-v11-text-muted mt-1">
                    {error instanceof Error ? error.message : 'Unknown error'}
                  </p>
                </div>
              ) : (
                <div className={isFetching ? 'opacity-50 transition-opacity' : ''}>
                  <DataTable
                    data={filteredData}
                    columns={columns}
                    enableSorting
                    manualSorting={true}
                    onSortChange={handleSortChange}
                    enablePagination={false}
                    enableSearch={false}
                    enableColumnVisibility
                    tableId="felton-channels"
                    emptyMessage="No channels configured"
                    onTableReady={handleTableReady}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                    meta={{ currentPeriod: period }}
                    maxHeight="calc(100vh - 300px)"
                    footer={
                      currentData.length > 0 && channelsData?.meta?.hasMorePages ? (
                        <div className="border-t border-v11-border px-4 py-3 flex items-center justify-between">
                          <span className="text-sm text-v11-text-secondary">
                            Showing {currentData.length} of {channelsData.meta.total} channels
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={isFetching}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-v11-surface-hover hover:bg-v11-surface-active text-v11-text-primary text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {isFetching ? 'Loading...' : 'Load More'}
                          </button>
                        </div>
                      ) : undefined
                    }
                  />
                </div>
              )}
            </>
          )}

          {/* Card Grid View */}
          {viewMode === 'chart' && (
            <>
              <ChannelCardGrid
                channels={currentData}
                miniChartData={miniChartData}
                periodLabel={periodLabel}
                period={period}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                includeOwnChannels={includeOwnChannels}
                onIncludeOwnChannelsChange={setIncludeOwnChannels}
                isPeriodLoading={isPeriodLoading}
                search={channelSearch}
                onSearchChange={setChannelSearch}
                platformFilter={platformFilter}
                onPlatformFilterChange={setPlatformFilter}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
